const DEFAULT_POLL_INTERVAL_MS = 5 * 60 * 1000
const DEFAULT_INACTIVITY_FORCE_MS = 10 * 60 * 1000
const AUTO_UPDATED_STORAGE_KEY = 'orbviz-app-auto-updated'

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll'] as const

export { AUTO_UPDATED_STORAGE_KEY }

export interface AppVersionMonitorOptions {
  indexUrl: string
  pollIntervalMs?: number
  inactivityForceMs?: number
  onUpdateAvailable: (actions: { reload: () => void }) => void
  onForcedReload?: () => void
}

export function extractDeployedModuleScript(html: string): string | null {
  const match =
    html.match(/<script[^>]+type="module"[^>]+src="([^"]+)"/i) ??
    html.match(/<script[^>]+src="([^"]+)"[^>]+type="module"/i)
  const src = match?.[1]
  if (!src || src.includes('/src/')) {
    return null
  }
  return src
}

export function getCurrentModuleScriptSrc(): string | null {
  const script = document.querySelector('script[type="module"][src]')
  const src = script?.getAttribute('src')
  if (!src || src.includes('/src/')) {
    return null
  }
  return src
}

export function startAppVersionMonitor(options: AppVersionMonitorOptions): () => void {
  const pollIntervalMs = options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS
  const inactivityForceMs = options.inactivityForceMs ?? DEFAULT_INACTIVITY_FORCE_MS

  let updateDetected = false
  let lastActivityAt = Date.now()
  let inactivityTimer: ReturnType<typeof setTimeout> | undefined

  const reload = () => {
    window.location.reload()
  }

  const scheduleInactivityForce = () => {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer)
    }
    inactivityTimer = setTimeout(() => {
      if (!updateDetected) {
        return
      }
      if (Date.now() - lastActivityAt >= inactivityForceMs) {
        options.onForcedReload?.()
        reload()
        return
      }
      scheduleInactivityForce()
    }, inactivityForceMs)
  }

  const recordActivity = () => {
    lastActivityAt = Date.now()
    if (updateDetected) {
      scheduleInactivityForce()
    }
  }

  const checkForUpdate = async () => {
    if (document.visibilityState === 'hidden') {
      return
    }

    try {
      const response = await fetch(options.indexUrl, { cache: 'no-store' })
      if (!response.ok) {
        return
      }

      const deployedSrc = extractDeployedModuleScript(await response.text())
      const currentSrc = getCurrentModuleScriptSrc()
      if (!deployedSrc || !currentSrc || deployedSrc === currentSrc) {
        return
      }

      if (!updateDetected) {
        updateDetected = true
        options.onUpdateAvailable({ reload })
        scheduleInactivityForce()
      }
    } catch {
      // Ignore transient network errors while polling.
    }
  }

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      void checkForUpdate()
    }
  }

  for (const event of ACTIVITY_EVENTS) {
    document.addEventListener(event, recordActivity, { passive: true })
  }
  document.addEventListener('visibilitychange', onVisibilityChange)

  void checkForUpdate()
  const pollTimer = setInterval(() => {
    void checkForUpdate()
  }, pollIntervalMs)

  return () => {
    if (pollTimer) {
      clearInterval(pollTimer)
    }
    if (inactivityTimer) {
      clearTimeout(inactivityTimer)
    }
    for (const event of ACTIVITY_EVENTS) {
      document.removeEventListener(event, recordActivity)
    }
    document.removeEventListener('visibilitychange', onVisibilityChange)
  }
}
